import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { autoPostAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';
import {
  X, Clock, Save,
  CheckCircle, XCircle, Loader2, Trash2, AlertCircle,
} from 'lucide-react';
import PerPlatformCaptionEditor from './PerPlatformCaptionEditor';

const EDITABLE_STATUSES = new Set([
  'pending', 'claiming', 'generating_video', 'preview_pending', 'video_ready', 'claiming_post',
]);

/**
 * Detail modal for a queue or history item.
 *
 * Behavior depends on item.status:
 *   - "preview_pending". Full editor: video player + per-platform
 *     caption textareas + Approve/Reject + Regenerate + Save + Delete.
 *   - other in-flight (pending, generating_video, video_ready, etc.) –
 *     same as above but no Approve/Reject (status doesn't allow it).
 *   - "rejected" / "completed" / "completed_partial" / "failed" –
 *     read-only view: video + captions visible, copy buttons, but no
 *     edit/regenerate/decision actions. Edits don't make sense after
 *     the post fired.
 *
 * Props
 *   item. The queue or history row to display. Must include id,
 *          status, scheduled_for, property_title, video_url?,
 *          caption?, hashtags?, per_platform_captions?
 *   onClose(). Close handler
 *   onChange(updatedItem). Fired after a successful PATCH/regenerate/
 *                          decision so the parent can refresh its list.
 *                          The full updated row is passed.
 *   onDelete(id). Optional; renders a Delete button when provided
 *                  AND the status allows deletion.
 */
export default function AutoPostItemDetailModal({ item, previewMode = 'auto_with_preview', onClose, onChange, onDelete }) {
  const { t } = useTranslation();

  // Working copy of the per-platform captions + flat caption/hashtags.
  // We init from `item` and mutate as the user edits; Save sends the
  // diff back via PATCH.
  const [perPlatform, setPerPlatform] = useState(item.per_platform_captions || {});
  const [flatCaption, setFlatCaption] = useState(item.caption || '');
  const [flatHashtags, setFlatHashtags] = useState(item.hashtags || []);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [decisionPending, setDecisionPending] = useState(false);

  const isEditable = EDITABLE_STATUSES.has(item.status);
  const isPreviewPending = item.status === 'preview_pending';

  // Reset working state when the item changes (e.g. after regenerate
  // bubbles a fresh `item` in from the parent). PerPlatformCaptionEditor
  // manages its own active-tab + copy-state internally.
  useEffect(() => {
    setPerPlatform(item.per_platform_captions || {});
    setFlatCaption(item.caption || '');
    setFlatHashtags(item.hashtags || []);
  }, [item]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Dirty detection. Only relevant for editable items. Saves are
  // explicit; we don't autosave on every keystroke (would spam the
  // backend + create surprising state).
  const isDirty = (() => {
    if (!isEditable) return false;
    if ((item.caption || '') !== flatCaption) return true;
    const origPlat = item.per_platform_captions || {};
    const keys = new Set([...Object.keys(origPlat), ...Object.keys(perPlatform)]);
    for (const k of keys) if ((origPlat[k] || '') !== (perPlatform[k] || '')) return true;
    return false;
  })();

  const handleSave = async () => {
    if (!isDirty) return;
    setSaving(true);
    try {
      const patch = {};
      if ((item.caption || '') !== flatCaption) {
        patch.caption = flatCaption;
        patch.hashtags = flatHashtags;
      }
      // Always include per_platform_captions if any platform differs
      const origPlat = item.per_platform_captions || {};
      const keys = new Set([...Object.keys(origPlat), ...Object.keys(perPlatform)]);
      let perDiffers = false;
      for (const k of keys) if ((origPlat[k] || '') !== (perPlatform[k] || '')) { perDiffers = true; break; }
      if (perDiffers) patch.per_platform_captions = perPlatform;

      const res = await autoPostAPI.patchQueueItem(item.id, patch);
      toast.success(t('autoPost.detail.saved') || 'Captions gespeichert.');
      onChange?.(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === 'object' && detail.message) || t('autoPost.detail.saveFailed');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async () => {
    if (!isEditable) return;
    if (isDirty) {
      // Warn but proceed. We don't want to silently throw away edits.
      const confirmed = window.confirm(
        t('autoPost.detail.regenerateWarn') ||
        'Du hast ungespeicherte Änderungen. Trotzdem neu generieren? (verwirft deine Bearbeitung)'
      );
      if (!confirmed) return;
    }
    setRegenerating(true);
    try {
      const res = await autoPostAPI.regenerateCaptions(item.id);
      toast.success(t('autoPost.detail.regenerated') || 'Captions neu generiert.');
      onChange?.(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === 'object' && detail.message) || t('autoPost.detail.regenerateFailed');
      toast.error(msg);
    } finally {
      setRegenerating(false);
    }
  };

  const handleDecision = async (decision) => {
    setDecisionPending(true);
    try {
      const res = await autoPostAPI.setQueueDecision(item.id, decision);
      toast.success(
        decision === 'approved'
          ? (t('autoPost.detail.approved') || 'Post bestätigt. Wird zur geplanten Zeit veröffentlicht.')
          : (t('autoPost.detail.rejected') || 'Post abgebrochen.')
      );
      onChange?.(res.data);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === 'object' && detail.message) || t('autoPost.detail.decisionFailed');
      toast.error(msg);
    } finally {
      setDecisionPending(false);
    }
  };

  // Copy handling lives inside PerPlatformCaptionEditor. No need
  // for a local handleCopy here anymore.

  // Scheduled time formatter. Same logic as the queue row's
  // (handles BSON Date as naive UTC).
  const scheduledStr = (() => {
    const raw = item.scheduled_for;
    if (!raw) return '–';
    const s = String(raw);
    const fixed = /[Zz]|[+-]\d\d:?\d\d$/.test(s) ? s : s + 'Z';
    try {
      return new Date(fixed).toLocaleString(undefined, {
        weekday: 'short', day: '2-digit', month: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    } catch (_) {
      return String(raw);
    }
  })();

  // The platforms this item targets. Used to filter the per-platform
  // tabs so we don't show variants for platforms the user isn't even
  // posting to.
  const targetPlatforms = Array.isArray(item.platforms) && item.platforms.length
    ? item.platforms
    : Object.keys(perPlatform);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="glass border-2 border-[#1f4d3a]/30 w-full sm:max-w-3xl max-h-[92dvh] sm:max-h-[90dvh] overflow-y-auto overscroll-contain rounded-t-2xl sm:rounded-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-[#fffdf7]/95 backdrop-blur-md border-b border-[#e3dccb] px-5 sm:px-6 py-4 flex items-center justify-between z-10 gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-[#23211a] truncate">
              {item.property_title || t('autoPost.property') || 'Immobilie'}
            </h2>
            <p className="text-xs text-[#6f6a5c] mt-0.5 flex items-center gap-2 flex-wrap">
              <Clock className="w-3 h-3" />
              <span>{scheduledStr}</span>
              <StatusPill status={item.status} previewMode={previewMode} t={t} />
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-[#efe9db] text-[#6f6a5c] hover:text-[#23211a] transition shrink-0"
            aria-label={t('common.close') || 'Close'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 sm:px-6 py-5 space-y-4">
          {/* Read-only banner for non-editable items so the user
              understands why edit fields might be missing/disabled. */}
          {!isEditable && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-[#f2ecdf] border border-[#d8d0bd]">
              <AlertCircle className="w-4 h-4 text-[#6f6a5c] mt-0.5 shrink-0" />
              <p className="text-xs text-[#4a463b]">
                {item.status === 'completed' || item.status === 'completed_partial'
                  ? (t('autoPost.detail.readOnlyCompleted') || 'Dieser Post ist bereits veröffentlicht und kann nicht mehr bearbeitet werden.')
                  : item.status === 'rejected'
                  ? (t('autoPost.detail.readOnlyRejected') || 'Dieser Post wurde abgebrochen.')
                  : (t('autoPost.detail.readOnlyOther') || 'Dieser Post ist nicht mehr bearbeitbar.')}
              </p>
            </div>
          )}

          {/* Video player. Only renders when video_url is available
              (item.video_url exists for preview_pending+ states). */}
          {item.video_url ? (
            <div className="rounded-xl overflow-hidden bg-black border border-[#e3dccb] aspect-[9/16] max-h-[60vh] mx-auto">
              <video
                src={item.video_url}
                controls
                playsInline
                className="w-full h-full object-contain bg-black"
              />
            </div>
          ) : item.status === 'pending' || item.status === 'generating_video' ? (
            <div className="rounded-xl border border-[#e3dccb] bg-[#f2ecdf] p-8 text-center text-sm text-[#6f6a5c]">
              <Loader2 className="w-8 h-8 mx-auto mb-2 text-[#1f4d3a] animate-spin" />
              <p>{t('autoPost.detail.videoRendering') || 'Video wird gerade erstellt. Vorschau erscheint hier sobald fertig.'}</p>
            </div>
          ) : null}

          {/* Per-platform caption editor. Shared component used by
              both this modal AND the email-landing PreviewPage so the
              editor behaves identically wherever the user reviews a
              post. We pass the working copy + onChange so dirty
              detection still happens at this level. */}
          {targetPlatforms.length > 0 && (
            <PerPlatformCaptionEditor
              perPlatformCaptions={perPlatform}
              targetPlatforms={targetPlatforms}
              onChange={setPerPlatform}
              readOnly={!isEditable}
              onRegenerate={isEditable ? handleRegenerate : undefined}
              regenerating={regenerating}
            />
          )}

          {/* Legacy fallback for rows without per-platform captions
              (rendered before the feature existed). Shows the flat
              caption + hashtags as one editor. */}
          {targetPlatforms.length === 0 && (flatCaption || flatHashtags.length > 0) && (
            <div className="rounded-lg border border-[#e3dccb] bg-[#f7f3ea]/40 p-3">
              <textarea
                value={flatCaption}
                onChange={(e) => setFlatCaption(e.target.value)}
                readOnly={!isEditable}
                rows={6}
                className="w-full bg-transparent border-0 text-sm text-[#23211a] focus:outline-none focus:ring-0 resize-y"
              />
              {flatHashtags.length > 0 && (
                <p className="text-xs text-[#6f6a5c] mt-2">
                  {flatHashtags.join(' ')}
                </p>
              )}
            </div>
          )}

          {/* Failure context */}
          {item.error_message && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-[#b3543f]/5 border border-[#b3543f]/40">
              <XCircle className="w-4 h-4 text-[#b3543f] mt-0.5 shrink-0" />
              <div className="text-xs text-[#8c3a29]">
                <p className="font-medium mb-0.5">{t('autoPost.detail.errorLabel') || 'Fehlermeldung'}</p>
                <p>{item.error_message}</p>
              </div>
            </div>
          )}
        </div>

        {/* Sticky action footer */}
        <div className="sticky bottom-0 bg-[#fffdf7]/95 backdrop-blur-md border-t border-[#e3dccb] px-5 sm:px-6 py-3 z-10 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            {onDelete && (item.status === 'pending' || item.status === 'preview_pending' || item.status === 'failed') && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => { onDelete(item.id); onClose?.(); }}
                className="text-[#b3543f] hover:text-[#a34430] hover:bg-[#f7e8e3]"
                data-testid="detail-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-1.5" />
                {t('common.delete') || 'Löschen'}
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {isPreviewPending && (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => handleDecision('rejected')}
                  disabled={decisionPending}
                  className="border-[#b3543f]/50 text-[#8c3a29] hover:bg-[#f7e8e3]"
                  data-testid="detail-reject-btn"
                >
                  <XCircle className="w-4 h-4 mr-1.5" />
                  {previewMode === 'require_approval'
                    ? (t('autoPost.detail.reject') || 'Ablehnen')
                    : (t('autoPost.detail.stop') || 'Stoppen')}
                </Button>
                {/* v1.6 – "Bestätigen" is a no-op in auto_with_preview
                    (silence == approval already), so showing it as a
                    prominent CTA misleads users into thinking approval
                    is required. Only render in require_approval. */}
                {previewMode === 'require_approval' && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => handleDecision('approved')}
                    disabled={decisionPending}
                    className="bg-[#1e7a4f] hover:bg-[#1e7a4f] text-white"
                    data-testid="detail-approve-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-1.5" />
                    {t('autoPost.detail.approve') || 'Freigeben'}
                  </Button>
                )}
              </>
            )}
            {isEditable && !isPreviewPending && (
              <Button
                type="button"
                size="sm"
                onClick={handleSave}
                disabled={!isDirty || saving}
                className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white"
                data-testid="detail-save-btn"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />{t('common.saving')}</>
                ) : (
                  <><Save className="w-4 h-4 mr-1.5" />{t('common.save')}</>
                )}
              </Button>
            )}
            {isPreviewPending && isDirty && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={handleSave}
                disabled={saving}
                className="text-[#4a463b] hover:text-[#23211a] hover:bg-[#efe9db]"
                title={t('autoPost.detail.saveBeforeDecision') || 'Änderungen vor dem Bestätigen speichern'}
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />{t('common.saving')}</>
                ) : (
                  <><Save className="w-4 h-4 mr-1.5" />{t('common.save')}</>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// Small status chip. Extracted so it can sit inline in the header.
// Mirrors the styling used in the queue row but smaller.
function StatusPill({ status, previewMode = 'auto_with_preview', t }) {
  const styleMap = {
    pending: 'bg-[#f0e2c2] text-[#9a6d1f]',
    claiming: 'bg-[#f0e2c2] text-[#9a6d1f]',
    generating_video: 'bg-[#e9efe7] text-[#1f4d3a]',
    preview_pending: 'bg-sky-600/20 text-sky-300',
    video_ready: 'bg-[#f0e6cf] text-[#8f6f3a]',
    claiming_post: 'bg-blue-600/20 text-blue-300',
    posting: 'bg-blue-600/20 text-blue-300',
    completed: 'bg-[#d6e8dd] text-[#1e7a4f]',
    completed_partial: 'bg-[#f0e2c2] text-[#9a6d1f]',
    rejected: 'bg-[#c9c1ac]/20 text-[#4a463b]',
    cancelled: 'bg-[#c9c1ac]/20 text-[#4a463b]',
    failed: 'bg-[#a34430]/20 text-[#a34430]',
  };
  const cls = styleMap[status] || 'bg-[#c9c1ac]/20 text-[#4a463b]';
  // v1.6. Preview_pending means very different things per mode (see
  // queue row in AutoPostSettingsPage for the longer comment).
  const labelKey = status === 'preview_pending' && previewMode !== 'require_approval'
    ? 'autoPost.status.preview_pending_auto'
    : `autoPost.status.${status}`;
  return (
    <span className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full ${cls}`}>
      {t(labelKey) || status}
    </span>
  );
}
